import {createSelector} from 'reselect'

import {
  getState,
  setForm,
  updateForm,
  removeForm,
  formsSelector,
  onlyIfForm,
} from '../../../store.js'
import {
  STOCKTAKE_INITIATED_DISPLAY,
  STOCKTAKE_STARTED_DISPLAY,
  STOCKTAKE_COMPLETED_DISPLAY,
  STOCKTAKE_CANCELLED_DISPLAY,
} from '../../../common/constants/Stocktakes.js'
import {PluralBlock, Plural, plural} from '../../../common/components/Plural.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import apiverson from '../../../common/apiverson.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import {
  setStocktake,
  stocktakeStatusSelector,
} from '../../../data/stocktakes.js'
import {refreshStocktakeList} from '../stocktakeListFunctions.js'
import {getRealDate} from '../../../common/date.js'
import {currentPageSelector} from '../../ReturnOrderListPage/returnOrderListSelectors.js'
import {STOCKTAKE_LIST_PAGE} from '../../../common/constants/Pages.js'
import {refreshStocktakeDetails} from '../../StocktakeDetailPage/stocktakeDetailFunctions.js'

const MODAL_FORM = 'update_stocktake_status_modal'

export function showUpdateStocktakeStatusModal(referenceIDs, status) {
  status ||= stocktakeStatusSelector(getState(), {referenceID: referenceIDs[0]})

  setForm(MODAL_FORM, {
    referenceIDs,
    status,
    isSaving: false,
    serverError: null,
  })
}

export function updateModalForm(updates) {
  updateForm(MODAL_FORM, updates)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export const modalFormSelector = createSelector(
  formsSelector,
  (forms) => forms[MODAL_FORM],
)

export async function updateStocktakeStatus(referenceID, params) {
  const {json} = await apiverson.put(
    `/stocktake/${encodeURIComponent(referenceID)}`,
    params,
  )

  setStocktake(json)
}

export function updateStocktakeStatusParams(status) {
  const now = getRealDate()
  const params = {}

  if (status === STOCKTAKE_INITIATED_DISPLAY) {
    params.started_date = null
    params.completed_date = null
    params.cancelled_date = null
  } else if (status === STOCKTAKE_STARTED_DISPLAY) {
    params.started_date = now.toISOString()
    params.completed_date = null
    params.cancelled_date = null
  } else if (status === STOCKTAKE_COMPLETED_DISPLAY) {
    params.completed_date = now.toISOString()
    params.cancelled_date = null
  } else if (status === STOCKTAKE_CANCELLED_DISPLAY) {
    params.cancelled_date = now.toISOString()
  }

  return params
}

export async function updateStocktakesStatus() {
  try {
    updateModalForm({isSaving: true})

    const {referenceIDs, status} = modalFormSelector(getState())
    const count = referenceIDs.length
    const params = updateStocktakeStatusParams(status)

    await Promise.all(
      referenceIDs.map((referenceID) =>
        updateStocktakeStatus(referenceID, params),
      ),
    )

    showMessageToast(
      plural(count)`${count} Stocktake${['s were', ' was']} updated.`,
    )

    closeModal()

    if (currentPageSelector(getState()) === STOCKTAKE_LIST_PAGE) {
      await refreshStocktakeList()
    } else {
      await refreshStocktakeDetails()
    }
  } catch (err) {
    updateModalForm({
      serverError: `Error setting Stocktake status: ${
        err.message || err.error_message
      }`,
      isSaving: false,
    })
  }
}

function UpdateStocktakeStatusModal({form}) {
  const count = form.referenceIDs.length

  return (
    <ConfirmModal
      title="Update Stocktake Status"
      confirmText="Save"
      cancelText="Cancel"
      onConfirm={() => updateStocktakesStatus()}
      onCancel={() => closeModal()}
      isSaving={form.isSaving}
      error={form.serverError}
    >
      <PluralBlock count={count}>
        <ul className="list list--no-style">
          {count === 1 && (
            <li className="list__item--form list__item--no-style divider--bottom">
              <div className="fs-01">
                <strong>{form.referenceIDs[0]}</strong>
              </div>
            </li>
          )}
          <li className="list__item--form list__item--no-style fs-01">
            Update the status for <Plural p={count} s="this" />{' '}
            <Plural word="Stocktake" />:
          </li>
          {[
            STOCKTAKE_INITIATED_DISPLAY,
            STOCKTAKE_STARTED_DISPLAY,
            STOCKTAKE_COMPLETED_DISPLAY,
            STOCKTAKE_CANCELLED_DISPLAY,
          ].map((status) => (
            <li className="list__item--no-style" key={status}>
              <label className="fs-00" htmlFor={status}>
                <input
                  className="margin-right-7"
                  type="radio"
                  name="return_order_line_code"
                  id={status}
                  value={status}
                  checked={status === form.status}
                  onChange={() => updateModalForm({status})}
                />
                {status}
              </label>
            </li>
          ))}
          {form.status === STOCKTAKE_CANCELLED_DISPLAY && (
            <li className="list__item--no-style">
              <div className="alert alert--standard margin-bottom-0">
                <div>
                  <strong>
                    Heads up — cancelling a stocktake does not affect inventory.
                  </strong>
                </div>
                If you’ve reconciled products associated with{' '}
                <Plural p="these" s="this" /> <Plural word="stocktake" />, the
                inventory values WILL NOT be reset.
              </div>
            </li>
          )}
        </ul>
      </PluralBlock>
    </ConfirmModal>
  )
}

export default onlyIfForm(UpdateStocktakeStatusModal, modalFormSelector)
